import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {Group,Box,Title,Text,ScrollArea, Grid, Button, Textarea} from "@mantine/core";
import { IconArrowLeft, IconStar, IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";

import { fetchGeneralEvaluation, getEvaluation, UpdateGeneralEvaluationObservation} from "../actions/evaluationActions";
import { GetAllCarreras } from "../actions/academicActions";
import { loadMatrixPei } from "../actions/peiActions";
import { loadEvidences } from "../actions/evidencesActions.js";

import useActiveProcess from '../hooks/useActiveProcess';
import { useActivePermission } from '../hooks/useActivePermission';
import useFeatureAccess from "../hooks/useFeatureAccess.js";

import { ATTRIBUTE_TYPES } from "../enums/AttributesTypes.js";
import { Category } from "../enums/PermissionsEnums.js";
import { isFacultyUnit } from "../utils/permissionHelpers.js";
import AssessmentPage from "./AssessmentPage";
import ImprovementPlanPage from "./ImprovementPlanPage";
import InfoCard from "../components/InfoCard.js";
import StepperButton from "../components/Evaluation/StepperButton.js";

const EvaluationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, permissions } = useSelector((state) => state.auth);
  const { generalEvaluation, fetching, saving } = useSelector((state) => state.evaluation );
  const { data: evaluation, loading: loadingEvaluation } = useSelector((state) => state.evaluation.evaluation);
  const { data: carreras, loading: loadingCarreras} = useSelector((state) => state.academic);
  
  const activeProcess = useActiveProcess();
  const activePermission = useActivePermission();
  const { canEnter, canEdit, readOnly, reason } = useFeatureAccess('evidences');   // { canEnter, canEdit, readOnly, reason }

  const [ listedEvaluations, setListedEvaluations ] = useState([]);
  const [ activeEvaluation, setActiveEvaluation ] = useState(null);
  const [ activePage, setActivePage ] = useState("AssessmentPage");

  const [obs, setObs] = useState("");

  const isFaculty = isFacultyUnit(activePermission);
  const weaknessCount = useMemo(() =>
      evaluation?.cualidades?.filter((attr) => attr.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.DEBILIDAD).length || 0,
    [evaluation?.cualidades]
  );
  const strengthsCount = useMemo(() =>
      evaluation?.cualidades?.filter((attr) => attr.tipoAtributo.Id.toUpperCase() === ATTRIBUTE_TYPES.FORTALEZA).length || 0,
    [evaluation?.cualidades]
  );
  const pendingWeakness = useMemo(() => {
    if (!evaluation) return [];

    const allWeakness = (evaluation.cualidades ?? []).filter(
      (c) => c?.tipoAtributo?.Id?.toUpperCase() === ATTRIBUTE_TYPES.DEBILIDAD && !c.asignadoPlanMejora
    );

    return allWeakness;
  }, [evaluation]);

  const fetchCarreras = useCallback((unitType,codUnit, codSede) => {
    
    if(!unitType || !codUnit) return;

    if(unitType.toUpperCase() === Category.FACULTAD){
      dispatch(GetAllCarreras(null,codUnit,null,user))
    } else if (unitType.toUpperCase() === Category.CARRERA){
      dispatch(GetAllCarreras(codUnit,null,codSede,user))
    }
  }, [permissions.data, user]);

  useEffect(() => {
    if(canEnter){
      const codUnit = activePermission?.permiso?.EntityId || null;
      const codSede = activePermission?.sede?.codigo || null;

      dispatch(fetchGeneralEvaluation(activeProcess.Id,codUnit,codSede,user));
      dispatch(loadMatrixPei(activeProcess.matriz.Id,user));
      dispatch(loadEvidences(null,activeProcess.Id,user,codUnit,codSede));
      fetchCarreras(activePermission.permiso.categoria?.Id, codUnit, codSede);
    }else{
      navigate('/backoffice')
    }
  }, [canEnter]);

  useEffect(() => {
    if (generalEvaluation?.evaluaciones?.length > 0) {
      const ordered = [...generalEvaluation.evaluaciones].sort(
        (a, b) => a.criterio.numCriterio - b.criterio.numCriterio
      );
      setListedEvaluations(ordered);

      if (!activeEvaluation) {
        setActiveEvaluation(ordered[0]);
        dispatch(getEvaluation(ordered[0].Id, user));
      }
    }
    if (generalEvaluation?.observacion) { setObs(generalEvaluation.observacion); }
  }, [generalEvaluation, dispatch, user]);

  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef((evaluation?.observacion ?? "").trim());

  // función que realmente guarda
  const saveObservation = useCallback((text) => {
    if (!evaluation?.Id) return;
    const clean = (text ?? "").trim();
    if (clean === lastSavedRef.current) return;

    return dispatch(UpdateGeneralEvaluationObservation(generalEvaluation.Id, clean, user))
      .then(() => { lastSavedRef.current = clean; })
  }, [dispatch, evaluation?.Id, user]);

  const handleObservationChange = (e) => {
    const val = e.currentTarget.value;
    setObs(val);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveObservation(val), 1800);
  };

  const flushObservationSave = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    return saveObservation(obs);
  }, [obs, saveObservation]);

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  const handleSelectEvaluation = useCallback(
    (evaluation) => {
      if (evaluation.Id === activeEvaluation?.Id) return;

      setActiveEvaluation(evaluation);
      dispatch(getEvaluation(evaluation.Id, user));
      setActivePage("AssessmentPage");
    },
    [dispatch, user]
  );
  
  const renderPlanStatus = (objectives) => {
    const objs = objectives || [];
    
    if (pendingWeakness.length === 0 && objs.some((obj) => obj.completado)) {
      return <Text inherit span c={"green"}> Completo</Text>;
    }
    if (objs.some((obj) => !obj.completado) && objs.length !== 0) {
      return <Text inherit span c={"yellow"}> En edición</Text>;
    }
    if (objs.length === 0) {
      return <Text inherit span c={"red"}> Incompleto</Text>;
    }
    return <Text inherit span c={"red"}> Incompleto</Text>;
  };
  
  const renderActivePage = () => {
    const pages = {
      AssessmentPage: (
        <AssessmentPage
          isFacultyUnit={isFaculty}
          navigateTo={() => setActivePage("ImprovementPlanPage")}
          editionEnabled={canEdit}
        />
      ),
      ImprovementPlanPage: (
        <ImprovementPlanPage
          navigateTo={() => setActivePage("AssessmentPage")}
          editionEnabled={canEdit}
        />
      ),
    };
  
    return pages[activePage] || null;
  };
  
  return (
    <div>
      <section className='bienvenida'>
        <div className='bienvenida-header'>
          <Group justify={'space-between'}>
            <Box >
              <Title fw={400} order={1} >Proceso de Evaluación • <Text span inherit c={'#E72166'}>{activePage == 'AssessmentPage' ? 'Valoracion de evidencias' : 'Plan de mejoras'}</Text></Title>
            </Box>
            {activePage=='ImprovementPlanPage' && (
              <Button 
                onClick={() => setActivePage('AssessmentPage')} 
                variant="default"
                leftSection={<IconArrowLeft/>}
              >
                Volver a Valoraciones, Debilidades y Fortalezas
              </Button>
            )}
          </Group>

          {activePage=='ImprovementPlanPage' && (
            <Box>
              <Textarea
                placeholder='Observaciones complementarias a todos los planes de mejora'
                autosize
                minRows={2}
                maxRows={5}
                maxLength={500}
                mt={'sm'}
                value={obs}
                onChange={handleObservationChange}
                onBlur={flushObservationSave}
                disabled={!evaluation?.Id || !canEdit} 
              />
              <Text size='xs' ta={'end'} c='dimmed' mt={4}>{saving ? 'Guardando...   ':''}{obs?.length} / 500</Text>
            </Box>
          )}
        </div>
          
        {!fetching && (
          <>
            <Text size='lg'>Seleccione el criterio de calidad a evaluar en base a las evidencias cargadas.</Text>
            <ScrollArea offsetScrollbars type='always' scrollbars="x">
              <Group grow wrap="nowrap" gap="xs" py={"xs"} px={2}>
                {listedEvaluations.map((e) => (
                  <StepperButton
                    key={e.criterio.Id}
                    label={`C${e.criterio.numCriterio}`}
                    isActive={e.criterio.Id === activeEvaluation?.criterio?.Id}
                    status={-1}
                    onClick={() => handleSelectEvaluation(e)}
                  />
                ))}
              </Group>
            </ScrollArea>
          </>
        )}
        <Grid>
          {isFaculty && (
            <Grid.Col span={{ base: 6, lg: 3 }}>
              <InfoCard
                title="Valoraciones"
                value={`${evaluation?.valoraciones?.length || 0} / ${carreras.length || 0}`}
                size="sm"
                icon={<IconStar size={22} stroke={1.5}/>}
                loading={fetching || loadingEvaluation}
              />
            </Grid.Col>
          )}
          <Grid.Col span={{ base: 6, lg: isFaculty ? 3 : 4, }}>
            <InfoCard
              title="Debilidades"
              value={weaknessCount}
              size="sm"
              icon={<IconTrendingDown size={22} stroke={1.5}/>}
              loading={fetching || loadingEvaluation}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, lg: isFaculty ? 3 : 4, }}>
            <InfoCard
              title="Fortalezas"
              value={strengthsCount}
              size="sm"
              icon={<IconTrendingUp size={22} stroke={1.5}/>}
              loading={fetching || loadingEvaluation}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, lg: isFaculty ? 3 : 4, }}>
            <InfoCard
              title="Plan de Mejora"
              value={renderPlanStatus(evaluation?.planMejoras)}
              size="sm"
              loading={fetching || loadingEvaluation}
            />
          </Grid.Col>
        </Grid>
      </section>
      <div>
        {renderActivePage()}
      </div>
    </div>
  );
};

export default EvaluationPage;
